/**
 * Norwegian locale identifier for sorting and formatting
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl#locales_argument
 */
export const NORWEGIAN_LOCALE = 'nb-NO' as const

/**
 * Standard collation options for Norwegian text comparison
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/Collator/Collator
 */
export const NORWEGIAN_COLLATOR_OPTIONS: Intl.CollatorOptions = {
  sensitivity: 'base',
  ignorePunctuation: true,
} as const

/**
 * Norwegian collator for efficient repeated comparisons
 */
const norwegianCollator = new Intl.Collator(NORWEGIAN_LOCALE, NORWEGIAN_COLLATOR_OPTIONS)

/**
 * Compare two strings using Norwegian locale and standard collation rules
 *
 * @param a - First string to compare
 * @param b - Second string to compare
 *
 * @returns Negative if a < b, positive if a > b, zero if equal
 */
export function compareLocaleNorwegian(a: string, b: string): number {
  return norwegianCollator.compare(a, b)
}

/**
 * Normalizes Norwegian text for search matching with aa/å equivalence
 *
 * Norwegian names can be spelled with either "å" or "aa" (historical variants).
 * This function normalizes both to "aa" for consistent bidirectional matching:
 * - Searching "å" will match names with "aa" (e.g., "Aasen")
 * - Searching "aa" will match names with "å" (e.g., "Åsen")
 *
 * @param text - The text to normalize for Norwegian search
 * @returns Normalized lowercase text with å→aa conversion
 *
 * @see normalizeNorwegianSort for the opposite transformation used in sorting
 */
export function normalizeNorwegianSearchText(text: string): string {
  return text.toLowerCase().replace(/å/g, 'aa')
}

export function normalizeSearchText(text: string): string {
  return text.toLowerCase().replace(/-/g, ' ').replace(/å/g, 'aa').replace(/\s+/g, ' ').trim()
}

export function matchesAllSearchTerms(input: string, fields: string[]): boolean {
  const terms = normalizeSearchText(input).split(/\s+/).filter(Boolean)
  return terms.every(term => fields.some(field => normalizeSearchText(field).includes(term)))
}

/**
 * Normalizes Norwegian text for alphabetical sorting with aa/å grouping
 *
 * Converts "aa" to "å" so that historical "aa" spellings sort together
 * with modern "å" spellings at the end of the Norwegian alphabet.
 * Used with compareLocaleNorwegian() for proper Norwegian sort order.
 *
 * @param text - The text to normalize for Norwegian sorting
 * @returns Text with aa -> å conversion for proper alphabetical ordering
 *
 * @see normalizeNorwegianSearchText for the opposite transformation used in search
 * @see compareLocaleNorwegian for Norwegian locale-aware comparison
 */
export function normalizeNorwegianSort(text: string): string {
  return text.replace(/aa/gi, match => match[0] === 'A' ? 'Å' : 'å')
}

/**
 * Returns last item in path ("/first/second/last") with optional typing
 */
export function lastInPath<T extends string>(path: string): T {
  if (!path.includes('/')) {
    return path as T
  }
  if (path.endsWith('/')) {
    path = path.substring(0, path.length - 1) as T
  }
  const parts = path.split('/')
  return parts[parts.length - 1] as T
}

export function isString(value: any): value is string {
  return typeof value === 'string'
}

export function ensureStartsWith(text: string, char: string): string {
  if (text.charAt(0) !== char) {
    return `${char}${text}`
  }
  return text
}

export function richTextIsEmpty(richText: string | undefined): boolean {
  if (!richText) {
    return true
  }
  return richText === '<?xml version="1.0" encoding="UTF-8"?>\n'
}

/**
 * Converts a delimited string to camelCase.
 *
 * @param input - The input string in a delimited format (e.g., kebab-case, snake_case).
 * @param delimiter - The delimiter used in the input string (default is '-').
 * @returns The camelCase version of the input string.
 */
export function toCamelCase(input: string, delimiter: string = '-'): string {
  const regex = new RegExp(`${delimiter}([a-zA-Z])`, 'g')
  return input.replace(regex, (_, char) => char.toUpperCase())
}

/**
 * Replaces variables in a string with their values
 *
 * @param raw - a string potentially containing variables in the form of `{{ variableKey }}`
 * @param variables - an object containing the values to replace the variables with, i.e. `{ variableKey: 'value' }`
 *
 * @returns the string with the variables replaced, or the original string if no variables were found
 */
export function interpolate<T extends string | undefined | null>(raw: T, variables: Record<string, string | number> | undefined): T {
  if (!raw || !variables) {
    return raw
  }

  // @ts-expect-error I don't think this is possible to type correctly
  return raw.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => {
    return key in variables ? variables[key] : `{{${key}}}`
  }) as T
}

const TEXTUAL_NUMBERS = [
  'ingen',
  'én',
  'to',
  'tre',
  'fire',
  'fem',
  'seks',
  'syv',
  'åtte',
  'ni',
  'ti',
  'elleve',
  'tolv',
] as const

const { format: formatNumber } = new Intl.NumberFormat('nb-NO', {
  style: 'decimal',
})

export function formatNumberAsText(value: number) {
  return TEXTUAL_NUMBERS[value] || formatNumber(value)
}

export function truncateText(text: string, maxLength: number, ellipsis: string = '...') {
  const truncated = text.length > maxLength
    ? `${text.slice(0, maxLength)}${ellipsis}`
    : text

  return { truncated }
}

/**
 *  Primarily for cleaning up Solr rich text responses in legal commentaries, but may apply elsewhere.
 *  @param text - The input string to be normalized (i.e. bodytext from Solr).
 *  @returns A string with cleaned HTML attributes.
 */
export function normalizeSolrRichText(text: string, options?: { preserveBr?: boolean }): string {
  // Normalize line breaks/spaces
  let normalized = normalizeLineBreaks(text, options)

  // Convert noisy HTML attributes from Ibexa like class="\&quot;Normal\&quot;" => class="Normal"
  normalized = normalizeHtmlAttributes(normalized)

  return normalized
}

/**
 * NS standards use an inline list-marker convention written as `<strong>a.</strong> Foo`
 * — but house style is plain `a)` (not bolded). Rewrite the strong-wrapped marker to
 * a paren-suffixed plain marker, leaving multi-segment numbering like
 * `<strong>1.1.</strong>` alone.
 */
export function convertNsListMarkers(html: string): string {
  return html.replace(/<strong>([a-z]+|\d+)\.<\/strong>/gi, '$1)')
}

/**
 * NS bodytext often closes each `<li>` with a `<br>` (or the malformed `</br>`).
 * The list-item box already provides the break, so strip those tags inside any
 * `<ol>` block. Other `<br>` occurrences in the bodytext are left untouched.
 */
export function stripBrInOrderedLists(html: string): string {
  return html.replace(/<ol\b[^>]*>[\s\S]*?<\/ol>/gi, block =>
    block.replace(/<\/?br\s*\/?>/gi, ''))
}

/**
 * NS bodytext uses `<br>` (and the malformed `</br>`) where authors mean
 * paragraph breaks. `<br>` is rendered as a non-stylable line-break primitive
 * in Chrome — `display: block` + `margin-top` is ignored — so swap each one
 * for a styleable empty `<span>` instead. Run after `stripBrInOrderedLists`
 * so list-item breaks are already gone.
 */
export function convertNsBrToBreaks(html: string): string {
  return html.replace(/<\/?br\s*\/?>/gi, '<span class="ns-br" aria-hidden="true"></span>')
}

/**
 * Remove trailing period/full stop from a string, if present
 * Useful for cleaning author descriptions where periods should be omitted
 *
 * @param text - The input string to clean
 * @returns The cleaned string without a trailing period
 */
export function removeTrailingPeriod(text: string): string {
  if (typeof text !== 'string') {
    return text
  }

  return text.trimEnd().replace(/\.$/, '')
}

const norwegianDisplayNames = new Intl.DisplayNames([NORWEGIAN_LOCALE], { type: 'language' })

/**
 * Formats a language code into its Norwegian display name
 *
 * @param code - The language code to format (e.g., 'en', 'no', 'de')
 * @param capitalize - Whether to capitalize the first letter of the result
 * @returns The localized language name in Norwegian
 *
 * @example
 * formatLanguageCode('en') // returns 'engelsk'
 * formatLanguageCode('no') // returns 'norsk'
 */
export function formatLanguageCode(code?: string, capitalize = false) {
  try {
    // Normalize and validate the locale (e.g. "EN-us" → "en-US")
    const [canonicalLocale] = Intl.getCanonicalLocales(code)

    // The language part is always before the first dash (e.g. "en" from "en-US")
    const languageCode = canonicalLocale?.split('-')[0] ?? ''

    // Convert "en" → "engelsk", "nb" → "norsk bokmål", etc.
    let languageName = norwegianDisplayNames.of(languageCode)
    if (capitalize && languageName) {
      languageName = languageName.charAt(0).toUpperCase() + languageName.slice(1)
    }

    return languageName
  }
  catch {
    // If the input is missing or not a valid locale, return undefined
    return undefined
  }
}

/**
 * Formats a list of strings using Norwegian list conventions.
 *
 * Rules:
 * - 0 items: empty string
 * - 1 item: "A"
 * - 2 items: "A og B"
 * - 3+ items: "A, B og C"
 *
 * @param items - The list items to format
 * @returns The formatted list string
 *
 * @example
 * formatNorwegianList([]) // ''
 * formatNorwegianList(['A']) // 'A'
 * formatNorwegianList(['A', 'B']) // 'A og B'
 * formatNorwegianList(['A', 'B', 'C']) // 'A, B og C'
 */
export function formatNorwegianList(items: string[]) {
  if (items.length === 0) {
    return ''
  }
  if (items.length === 1) {
    return items[0]
  }
  if (items.length === 2) {
    return `${items[0]} og ${items[1]}`
  }
  return `${items.slice(0, -1).join(', ')} og ${items[items.length - 1]}`
}
